import type { FleetLiveWire } from '@developerz/domain';
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { t } from '../i18n';
import { createElementWidth } from '../lib/element-width';
import {
  type CoreFocus,
  chooseSubject,
  coreHeight,
  coreModel,
  createBeams,
  dotPlan,
  FIRST_FOCUS,
  type GateSubject,
  holdFocus,
  nearestCluster,
  resolveSubject,
  tickFocus,
} from '../lib/fleet-live-core';
import { type ClusterCentre, createCorePainter } from '../lib/fleet-live-core-paint';
import type { LiveLoop } from '../lib/fleet-live-loop';
import { createMergeGate } from '../lib/merge-override';
import type { ToastTone } from '../lib/toast';
import { FleetLiveCanvas } from './FleetLiveCanvas';
import { FleetLiveCoreHead } from './FleetLiveCoreHead';
import { FleetLiveCoreText } from './FleetLiveCoreText';
import { FleetLiveGate } from './FleetLiveGate';
import { FleetLiveSquad } from './FleetLiveSquad';

// Core Intelligence (#6779, prototype `:3481-3504`, `:3685-3841`): the stage (its head, the
// canvas of every agent orbiting the eye in squad clusters, the hint, the panel controls)
// beside the squad feed and the Merge Gate. Container: it owns the squad focus, the Merge
// Gate's subject and the gate's read and write; the canvas, the feed and the gate render.
//
// FOCUS IS NOT THE SUBJECT. The focused squad (the canvas label and the feed) cycles every
// 8 s on the section's ONE frame loop, and a person's choice (a tab, a cluster click, a repo
// name) holds it 20 s. The Merge Gate's subject moves ONLY on a person's choice, never on
// the cycle, so the button cannot change repos under a pointer or a keyboard focus; while a
// save is in flight it does not move at all. Under reduced motion nothing cycles.

/** Below this container width (or a 1,000 px viewport) the side stacks under the stage. */
export const CORE_STACK_BELOW_PX = 900;

export type FleetLiveCoreProps = {
  readonly wire: FleetLiveWire;
  readonly loop: LiveLoop;
  readonly token: string | null;
  readonly canWrite: boolean;
  readonly notify: (message: string, tone?: ToastTone) => void;
  /** The panel's grip, Place and Hide buttons, set on the stage's top right. */
  readonly controls?: JSX.Element;
};

export function FleetLiveCore(props: FleetLiveCoreProps): JSX.Element {
  const tabs = createUniqueId();
  const model = createMemo(() => coreModel(props.wire));
  const plan = createMemo(() => dotPlan(model().squads));
  const painter = createCorePainter();
  const beams = createBeams();
  const grid = createElementWidth();
  const stage = createElementWidth(() => props.loop.invalidate());
  const [focus, setFocus] = createSignal<CoreFocus>(FIRST_FOCUS);
  const [chosen, setChosen] = createSignal<GateSubject | null>(null);
  const [hover, setHover] = createSignal(-1);
  let centres: readonly ClusterCentre[] = [];

  const subject = createMemo(() => resolveSubject(chosen(), model().squads));
  const gate = createMergeGate(
    props.token,
    () => subject()?.repo ?? null,
    (message, tone) => props.notify(message, tone),
  );
  const focused = (): number => {
    const n = model().squads.length;
    return n === 0 ? 0 : focus().index % n;
  };

  createEffect(
    on(
      () => props.wire.events,
      (events) =>
        beams.note(
          events,
          props.wire.items,
          model().squads,
          performance.now(),
          !props.loop.reducedMotion(),
        ),
    ),
  );
  onMount(() => {
    const release = props.loop.register((f) => {
      if (f.motion === null) return;
      const next = tickFocus(focus(), f.now, model().squads.length);
      if (next !== focus()) setFocus(next);
    });
    onCleanup(release);
  });

  /** A person chose squad `index` (and maybe one of its repos): hold the focus, move the subject. */
  const choose = (index: number, repo?: string): void => {
    const squad = model().squads[index];
    if (squad === undefined) return;
    setFocus(holdFocus(focus(), index));
    props.loop.invalidate();
    if (gate.save.pending()) return;
    setChosen(chooseSubject(model().squads, squad.key, repo));
  };
  const stacked = (): boolean =>
    grid.width() < CORE_STACK_BELOW_PX || (globalThis.innerWidth ?? 1_440) < 1_000;

  return (
    <div
      class="fleet-live__core"
      classList={{ 'fleet-live__core--stacked': stacked() }}
      ref={(el) => grid.attach(el)}
    >
      <div class="fleet-live__core-stage" ref={(el) => stage.attach(el)}>
        <FleetLiveCoreHead model={model()} controls={props.controls} />
        <FleetLiveCanvas
          label={t('fleet.live.core_canvas')}
          loop={props.loop}
          width={stage.width}
          height={coreHeight}
          paint={(ctx, p, w, f) => {
            centres = // `fitCanvas` hands back the canvas's own 2D context, typed down to `Paint2D`; the Core's
              // painters also call `ellipse`, `quadraticCurveTo`, gradients and `clip` on it.
              painter.paint(ctx as Parameters<typeof painter.paint>[0], {
                w,
                p,
                model: model(),
                plan: plan(),
                selected: focused(),
                hover: hover(),
                beams: beams.live(f.now),
                moving: f.motion !== null,
                now: f.now,
                dt: f.dt,
              });
          }}
          onPointerMove={(x, y, e) => {
            const i = nearestCluster(centres, x, y);
            (e.currentTarget as HTMLElement).style.cursor = i >= 0 ? 'pointer' : '';
            if (i !== hover()) {
              setHover(i);
              props.loop.invalidate();
            }
          }}
          onPointerLeave={() => {
            setHover(-1);
            props.loop.invalidate();
          }}
          onClick={(x, y) => {
            const i = nearestCluster(centres, x, y);
            if (i >= 0) choose(i);
          }}
        />
        <FleetLiveCoreText model={model()} />
        <p class="fleet-live__core-hint">{t('fleet.live.core_hint')}</p>
      </div>
      <div class="fleet-live__core-side">
        <FleetLiveSquad
          model={model()}
          focused={focused()}
          idPrefix={tabs}
          onSelect={(i) => choose(i)}
          onRepo={(squad, repo) => choose(model().squads.indexOf(squad), repo)}
        />
        <Show when={subject()}>
          {(s) => {
            const squad = () => model().squads.find((x) => x.key === s().squad);
            return (
              <FleetLiveGate
                gate={gate}
                repo={s().repo}
                canWrite={props.canWrite}
                lastMerge={squad()?.lastMerge?.number ?? null}
                atGate={squad()?.atGate ?? null}
              />
            );
          }}
        </Show>
      </div>
    </div>
  );
}
